/**
 * NotificationFeedback - AI recommendation + structured notification feedback.
 *
 * The AI recommendation is always visible (never collapsed).
 * The feedback form ("Rate this notification") is in a collapsible section below.
 * Includes a free-text textarea for additional context on submit.
 *
 * Submits feedback via the notification feedback endpoint.
 */

import { useState } from 'react';
import { notificationService } from '../../services/notifications';
import type { NotificationWorthiness, ActionUsefulness } from '../../services/notifications';
import AudioNotesInput from './AudioNotesInput';
import RecommendationText from './RecommendationText';
import styles from './NotificationFeedback.module.css';

interface NotificationFeedbackProps {
  /** UUID of the linked notification */
  notificationId: string;
  /** Ultra-short at-a-glance summary (plain text) */
  atAGlance: string | null;
  /** AI recommended action text */
  recommendedAction: string | null;
  /** AI recommended action reasoning */
  recommendedActionReasoning: string | null;
  /** Existing feedback state (thumbs_up / thumbs_down / null) */
  existingFeedback: string | null;
  /** Existing structured feedback */
  existingFeedbackWorth: string | null;
  existingFeedbackActionUseful: string | null;
  /** AI classification category (e.g. "order", "quote") - for category accuracy question */
  classificationCategory?: string | null;
}

type FeedbackState = 'idle' | 'submitting' | 'submitted';

const NotificationFeedback: React.FC<NotificationFeedbackProps> = ({
  notificationId,
  atAGlance,
  recommendedAction,
  recommendedActionReasoning,
  existingFeedback,
  existingFeedbackWorth,
  existingFeedbackActionUseful,
  classificationCategory,
}) => {
  const alreadyHasFeedback = !!(existingFeedback || existingFeedbackWorth);

  const [feedbackExpanded, setFeedbackExpanded] = useState(false);
  const [state, setState] = useState<FeedbackState>(
    alreadyHasFeedback ? 'submitted' : 'idle',
  );
  const [notificationWorth, setNotificationWorth] = useState<NotificationWorthiness | null>(
    (existingFeedbackWorth as NotificationWorthiness) || null,
  );
  const [actionUseful, setActionUseful] = useState<ActionUsefulness | null>(
    (existingFeedbackActionUseful as ActionUsefulness) || null,
  );
  const [categoryAccurate, setCategoryAccurate] = useState<boolean | null>(null);
  const [categoryOverride, setCategoryOverride] = useState<string>('');
  const [feedbackText, setFeedbackText] = useState('');

  const handleSubmit = async () => {
    setState('submitting');
    try {
      await notificationService.submitStructuredFeedback(notificationId, {
        notification_worth: notificationWorth || 'yes',
        action_useful: actionUseful || 'yes',
        category_accurate: categoryAccurate ?? undefined,
        category_override: categoryOverride || undefined,
        feedback_text: feedbackText || undefined,
      });
      setState('submitted');
    } catch (err) {
      console.error('Failed to submit notification feedback:', err);
      setState('idle');
    }
  };

  return (
    <div className={`${styles.section} ${state === 'submitted' ? styles.submitted : ''}`}>
      {/* AI Recommendation - always visible */}
      <div className={styles.recommendationArea}>
        <div className={styles.title}>
          <span className={styles.aiIcon}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
              <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z"/>
            </svg>
          </span>
          AI Recommendation
          {state === 'submitted' && <span className={styles.badge}>Rated</span>}
        </div>

        {/* At a Glance — ultra-short one-liner summary */}
        {atAGlance && (
          <div className={styles.recommendation}>
            <div className={styles.recommendationLabel}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
              </svg>
              At a Glance
            </div>
            <span className={styles.atAGlanceText}>{atAGlance}</span>
          </div>
        )}

        {/* Action Items — specific recommended actions */}
        {recommendedAction && (
          <div className={styles.recommendation}>
            <div className={styles.recommendationLabel}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z"/>
              </svg>
              Action Items
            </div>
            <RecommendationText text={recommendedAction} className={styles.recommendationText} />
          </div>
        )}

        {recommendedActionReasoning && !recommendedAction && !atAGlance && (
          <div className={styles.recommendation}>
            <div className={styles.recommendationLabel}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z"/>
              </svg>
              AI Reasoning
            </div>
            <RecommendationText text={recommendedActionReasoning} className={styles.recommendationText} />
          </div>
        )}
      </div>

      {/* Feedback section - collapsible */}
      <div className={styles.feedbackSection}>
        <button
          className={styles.feedbackToggle}
          onClick={() => setFeedbackExpanded((prev) => !prev)}
          type="button"
          aria-expanded={feedbackExpanded}
        >
          <span className={styles.feedbackToggleLabel}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            {state === 'submitted' ? 'Feedback submitted' : 'Rate this notification'}
          </span>
          <span className={`${styles.chevron} ${feedbackExpanded ? styles.chevronOpen : ''}`}>
            &#9660;
          </span>
        </button>

        {feedbackExpanded && (
          <div className={styles.feedbackBody}>
            {state === 'submitted' ? (
              <div className={styles.feedbackGiven}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
                <span>Thank you for your feedback!</span>
              </div>
            ) : (
              <>
                <div className={styles.feedbackDimension}>
                  <div className={styles.dimensionLabel}>Worth notifying?</div>
                  <div className={styles.dimensionOptions}>
                    {(['yes', 'maybe', 'no'] as const).map((value) => (
                      <button
                        key={value}
                        className={`${styles.dimensionBtn} ${notificationWorth === value ? styles.dimensionBtnActive : ''}`}
                        onClick={() => setNotificationWorth(value)}
                        type="button"
                      >
                        {value.charAt(0).toUpperCase() + value.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {recommendedAction && (
                  <div className={styles.feedbackDimension}>
                    <div className={styles.dimensionLabel}>Action useful?</div>
                    <div className={styles.dimensionOptions}>
                      {(['yes', 'partial', 'no'] as const).map((value) => (
                        <button
                          key={value}
                          className={`${styles.dimensionBtn} ${actionUseful === value ? styles.dimensionBtnActive : ''}`}
                          onClick={() => setActionUseful(value)}
                          type="button"
                        >
                          {value.charAt(0).toUpperCase() + value.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {classificationCategory && (
                  <div className={styles.feedbackDimension}>
                    <div className={styles.dimensionLabel}>
                      Category &ldquo;{classificationCategory}&rdquo; correct?
                    </div>
                    <div className={styles.dimensionOptions}>
                      <button
                        className={`${styles.dimensionBtn} ${categoryAccurate === true ? styles.dimensionBtnActive : ''}`}
                        onClick={() => { setCategoryAccurate(true); setCategoryOverride(''); }}
                        type="button"
                      >
                        Yes
                      </button>
                      <button
                        className={`${styles.dimensionBtn} ${categoryAccurate === false ? styles.dimensionBtnActive : ''}`}
                        onClick={() => setCategoryAccurate(false)}
                        type="button"
                      >
                        No
                      </button>
                    </div>
                    {categoryAccurate === false && (
                      <select
                        className={styles.categoryOverride}
                        value={categoryOverride}
                        onChange={(e) => setCategoryOverride(e.target.value)}
                      >
                        <option value="">What should it be?</option>
                        <option value="order">Order</option>
                        <option value="quote">Quote</option>
                        <option value="inquiry">Inquiry</option>
                        <option value="support">Support</option>
                        <option value="payment">Payment</option>
                        <option value="shipping">Shipping</option>
                        <option value="internal">Internal</option>
                        <option value="spam">Spam</option>
                        <option value="other">Other</option>
                      </select>
                    )}
                  </div>
                )}

                <AudioNotesInput
                  value={feedbackText}
                  onChange={setFeedbackText}
                  disabled={state === 'submitting'}
                />

                <button
                  className={styles.submitBtn}
                  onClick={handleSubmit}
                  disabled={state === 'submitting'}
                  type="button"
                >
                  {state === 'submitting' ? 'Submitting...' : 'Submit Feedback'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationFeedback;
